/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import * as Cesium from "cesium";
import proj from "fatproj4";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { setCesiumCoordinates } from "~/redux/reducers/mapReducer";
import { useAppDispatch } from "~/redux/store/store";
import {
  Custom3DTileset,
  CustomTerrainProvider,
} from "../types/CesiumMapItems";

type UseCesiumPositionTooltipReturn = {
  cesiumPositionTooltip: CesiumPositionTooltip;
};

type CesiumPositionTooltipArgs = {
  viewer: Cesium.Viewer;
  position: Cesium.Cartesian2;
};

type CesiumPositionTooltipReturn = void;
type CesiumPositionTooltip = (
  args: CesiumPositionTooltipArgs,
) => CesiumPositionTooltipReturn;

export function useCesiumPositionTooltip(): UseCesiumPositionTooltipReturn {
  const { t } = useTranslation("cesium");
  const dispatch = useAppDispatch();

  const cesiumPositionTooltip = useCallback(
    async ({
      viewer,
      position,
    }: CesiumPositionTooltipArgs): Promise<CesiumPositionTooltipReturn> => {
      if (!position) return;
      // Try getting the position of the pointcloud or from globe
      let carte3: Cesium.Cartesian3 | undefined;
      carte3 = viewer.scene.pickPosition(position);
      const feature = viewer.scene.pick(position);
      let projection: string | undefined;
      let totalShift: number | undefined;
      let featureExists = false;
      if (feature && feature.primitive instanceof Cesium.Cesium3DTileset) {
        const pc = feature.primitive as Cesium.Cesium3DTileset;
        const customPc = pc as Custom3DTileset;
        projection = customPc.projection;
        totalShift =
          (customPc.additionalHeightOffset + customPc.comparisonLevelOffset) /
          1000; // In meters
        featureExists = true;
      }
      if (!carte3 && !featureExists) {
        carte3 = viewer.camera.pickEllipsoid(
          position,
          viewer.scene.globe.ellipsoid,
        );
      }
      if (!carte3) {
        dispatch(setCesiumCoordinates(undefined));
        return;
      }
      const carto = Cesium.Cartographic.fromCartesian(carte3);
      let terrainCarto: Cesium.Cartographic | undefined;
      let terrainCarte3: Cesium.Cartesian3 | undefined;
      let terrainProj: string | undefined;
      await viewer.terrainProvider.readyPromise;
      if (viewer.terrainProvider.availability) {
        const terProj = (viewer.terrainProvider as CustomTerrainProvider)
          .projection;
        if (terProj) {
          terrainCarte3 = viewer.scene.globe.pick(
            viewer.camera.getPickRay(position),
            viewer.scene,
          );
          terrainProj = terProj;
        } else {
          const sample = await Cesium.sampleTerrainMostDetailed(
            viewer.terrainProvider,
            [carto],
          );
          terrainCarto = sample?.[0];
        }
      }
      const lon = Cesium.Math.toDegrees(
        terrainCarto?.longitude ?? carto.longitude,
      ).toFixed(5);
      const lat = Cesium.Math.toDegrees(
        terrainCarto?.latitude ?? carto.latitude,
      ).toFixed(5);
      const alt = terrainCarto?.height?.toFixed(2);

      let labelText = `${t(
        "wgs84-coordinates",
      )}:\nLon: ${lon}\u00B0 Lan: ${lat}\u00B0${
        alt !== undefined ? ` Alt: ${alt}` : ""
      }`;

      const unknownProjText = `${t("point-cloud", {
        projection: t("general:unknown"),
      })}:\nX: ${carte3.x.toFixed(3)}, Y: ${carte3.y.toFixed(
        3,
      )}, Z: ${carte3.z.toFixed(2)}`;

      if (featureExists) {
        if (projection) {
          const convCarte3 = proj(
            "EPSG:4978",
            projection,
            carte3,
          ) as Cesium.Cartesian3;
          labelText = convCarte3
            ? `${t("point-cloud", { projection })}:\nX: ${convCarte3.x.toFixed(
                3,
              )}, Y: ${convCarte3.y.toFixed(3)}, Z: ${
                totalShift
                  ? (convCarte3.z - totalShift).toFixed(2)
                  : convCarte3.z.toFixed(2)
              }${totalShift ? ` (${convCarte3.z.toFixed(2)})` : ""}`
            : unknownProjText;
        } else {
          labelText = unknownProjText;
        }
      } else if (terrainCarte3 && terrainProj) {
        const c = proj(
          "EPSG:4978",
          terrainProj,
          terrainCarte3,
        ) as Cesium.Cartesian3;
        if (c) {
          labelText = `${t("terrain-coordinate", {
            projection: terrainProj,
          })}:\nX: ${c.x.toFixed(3)}, Y: ${c.y.toFixed(3)}, Z: ${c.z.toFixed(
            2,
          )}`;
        }
      }
      dispatch(setCesiumCoordinates(labelText));
    },
    [dispatch, t],
  );

  return { cesiumPositionTooltip };
}
